import { formatDateToLocal } from '@safetyhub/shared';
import { Badge, Button, Card } from '@safetyhub/ui';
import {
  getUserIdentifier,
  getInvitationKindLabel,
  getMemberKindBadgeTone,
  getInvitationStatusBadgeTone,
  isOwnerInvitation,
  memberRoleBadges,
} from '../../../lib/organizations';
import { MembersLayoutProps } from './types';
import {
  getInvitationsAccess,
  getInvitationsDisplayState,
  INVITATIONS_FORBIDDEN_COPY,
  isOrganizationMember,
  shouldShowMemberGroups,
} from './membersView';
import { PlusIcon, ClockIcon, XCircleIcon, ChevronRightIcon, BuildingIcon } from './components/Icons';
import { PersonAvatar } from '../../../components/PersonAvatar';
import { AllModals } from './components/Modals';
import { MemberDedupEntry } from './components/MemberDedupEntry';
import { memberDisplayName, memberSearchName } from '../../../lib/members';
import { useAuthStore } from '../../../stores/auth';

export function MembersMobile({ data, modals, handlers, layout }: MembersLayoutProps) {
  const { members, invitations, currentUserId } = data;
  const companyId = useAuthStore((state) => state.currentMembership?.companyId);
  // 데스크톱 표의 '그룹' 칸과 같은 판단·같은 원천 — 그룹이 둘 이상일 때만 소속을 보인다.
  const companyGroupCount = useAuthStore(
    (state) => state.currentMembership?.company?.groups?.length ?? 0,
  );
  const showGroups = shouldShowMemberGroups(companyGroupCount);
  const isPhone = layout === 'phone';

  /**
   * 외부 contact(`role` null)는 조직 구성원이 아니라 결재선·현장 연락처용으로 연결만 해 둔
   * 행이라 멤버 목록에 서지 않는다 — 데스크톱 표(`membersView.buildMemberRows`)와 같은 규칙이다.
   * 헤더 카운트도 같은 배열을 세야 "N명"과 실제 줄 수가 갈리지 않는다.
   */
  const organizationMembers = members.filter(isOrganizationMember);

  /**
   * 초대 현황을 볼 수 있나 — `member.invite`가 없으면 `invitations`는 빈 게 아니라 묻지 않은 것이다
   * (`membersView.getInvitationsAccess` 주석). 데스크톱과 **같은 판정**을 쓴다.
   *
   * 모바일 섹션이 세는 축은 초대 **전량**이다(데스크톱 카드의 PENDING과 다르다 — 그래서 수를 인자로
   * 넘긴다). 세 상태 중 `empty`는 이 레이아웃에서 **아무것도 그리지 않는** 쪽이 원래 축이고, 그건
   * 「0」을 서술하는 자리가 없어 거짓말이 아니다 — `forbidden`만 말해 준다.
   */
  const invitationsAccess = getInvitationsAccess(data.canInviteMembers);
  const invitationsState = getInvitationsDisplayState(invitationsAccess, invitations.length);

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-bold tracking-tight text-ink-900 ${isPhone ? 'text-lg' : 'text-xl'}`}>멤버 관리</h1>
          {!isPhone && (
            <p className="text-sm font-medium text-ink-500">
              조직 멤버를 초대하고 역할을 관리합니다.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* 중복 인원 정리 (P5) — 클레임 조직 OWNER + 후보 존재 시에만 스스로 나타난다 */}
          {companyId && (
            <MemberDedupEntry companyId={companyId} onChanged={handlers.refreshMemberList} />
          )}
          {/* 서버 `POST :organizationId/members` = member.invite */}
          {data.canInviteMembers && (
            <Button
              variant="primary"
              size={isPhone ? 'sm' : 'md'}
              onClick={() => handlers.setShowInviteModal(true)}
            >
              <PlusIcon className={isPhone ? 'h-4 w-4' : 'h-5 w-5'} />
              {!isPhone && '초대'}
            </Button>
          )}
        </div>
      </div>

      {/* 멤버 목록 - 카드 */}
      <div className="space-y-2">
        <h2 className={`font-bold tracking-tight text-ink-900 ${isPhone ? 'text-sm' : 'text-base'}`}>
          멤버 ({organizationMembers.length}명)
        </h2>
        <div className="space-y-2">
          {organizationMembers.map((member) => {
            const isCurrentUser = member.userId != null && member.userId === currentUserId;

            return (
              <Card
                key={member.id}
                padding="sm"
                className="cursor-pointer active:bg-surface-hover"
                onClick={() => handlers.setDetailTarget(member)}
              >
                <div className="flex items-center">
                  {/* 아바타 — 데스크톱 표와 같은 6색 팔레트(사람 구분), 크기만 모바일 값 */}
                  <PersonAvatar
                    name={memberSearchName(member) || undefined}
                    seed={member.id}
                    size={isPhone ? 'md' : 'lg'}
                  />

                  {/* 정보 */}
                  <div className="ml-3 flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`font-bold tracking-tight text-ink-900 truncate ${isPhone ? 'text-sm' : ''}`}>
                        {memberDisplayName(member)}
                      </span>
                      {isCurrentUser && (
                        <span className="text-[10px] font-medium text-ink-400 flex-shrink-0">(나)</span>
                      )}
                      {member.user?.deletedAt && (
                        // Badge는 title을 받지 않아(코어 prop 최소화) 툴팁은 래퍼가 소유.
                        <span
                          className="inline-flex flex-none"
                          title={`탈퇴일: ${formatDateToLocal(new Date(member.user.deletedAt))}`}
                        >
                          <Badge tone="rose" dot>
                            탈퇴
                          </Badge>
                        </span>
                      )}
                      {/* 역할 배지 — 실제 역할 이름 전부(P7-b-2). 겸직이면 여러 개가 선다 */}
                      {memberRoleBadges(member).map((badge) => (
                        <Badge key={badge.key} tone={badge.tone} shape="rounded" className="flex-none">
                          {badge.label}
                        </Badge>
                      ))}
                    </div>
                    <div className={`font-medium text-ink-500 truncate ${isPhone ? 'text-xs' : 'text-sm'}`}>
                      {member.user ? getUserIdentifier(member.user.auths) : '-'}
                    </div>
                    {/* 소속 그룹 — 카드에는 빈 자리를 만들 이유가 없어 소속이 없으면(Owner
                        포함) 줄 자체를 내보내지 않는다. 데스크톱 표는 칸이 고정이라 `-`. */}
                    {showGroups && (member.groups?.length ?? 0) > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {member.groups?.map((group) => (
                          <Badge
                            key={group.id}
                            tone="teal"
                            shape="rounded"
                            size="sm"
                            className="max-w-[110px] truncate"
                          >
                            {group.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {(member.siteAssignments?.length ?? 0) > 0 && (
                      <div className={`flex items-center font-medium text-ink-400 mt-0.5 ${isPhone ? 'text-[10px]' : 'text-xs'}`}>
                        <BuildingIcon className="h-3 w-3 mr-1" />
                        {member.siteAssignments.length}개 현장
                      </div>
                    )}
                  </div>

                  <ChevronRightIcon className="h-4 w-4 text-ink-300 ml-2 flex-shrink-0" />
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* 초대 현황을 볼 권한이 없을 때 — 목록을 그리지 않는 것과 「초대가 없다」는 다른 상태다.
          데스크톱은 카드·탭 배지로도 같은 것을 말하지만, 모바일엔 그 둘이 없어 이 자리 하나다.
          판정은 데스크톱과 같은 `getInvitationsAccess`를 쓴다. */}
      {invitationsState === 'forbidden' && (
        <div className="space-y-2">
          <h2 className={`font-bold tracking-tight text-ink-900 ${isPhone ? 'text-sm' : 'text-base'}`}>
            초대 현황
          </h2>
          <Card padding="sm">
            <p className={`font-medium text-ink-500 ${isPhone ? 'text-xs' : 'text-sm'}`}>
              {INVITATIONS_FORBIDDEN_COPY}
            </p>
          </Card>
        </div>
      )}

      {/* 초대 현황 - 카드. `empty`(볼 수 있고 실제로 0건)면 섹션을 내보내지 않는다(모바일 원래 축 —
          데스크톱의 카드·탭과 달리 「0」을 서술하는 자리가 없어 그 자체로 거짓말이 아니다). */}
      {invitationsState === 'listed' && (
        <div className="space-y-2">
          <h2 className={`font-bold tracking-tight text-ink-900 ${isPhone ? 'text-sm' : 'text-base'}`}>
            초대 현황 ({invitations.length}건)
          </h2>
          <div className="space-y-2">
            {invitations.map((invitation) => {
              const isPending = invitation.status === 'PENDING';
              const isRejected = invitation.status === 'REJECTED';
              const isExpired = invitation.status === 'EXPIRED';

              return (
                <Card
                  key={invitation.id}
                  padding="sm"
                  className={!isPending ? 'opacity-60' : ''}
                >
                  <div className="flex items-center">
                    {/* 상태 아이콘 — 데스크톱 `.mem-av.c-pending`과 같은 점선 원(미가입 자리) */}
                    <div className={`rounded-full border-2 border-dashed flex items-center justify-center flex-shrink-0 ${isPhone ? 'h-8 w-8' : 'h-9 w-9'} ${
                      isPending ? 'border-amber-200 bg-amber-50' :
                      isRejected ? 'border-rose-200 bg-rose-50' :
                      'border-ink-300 bg-ink-100'
                    }`}>
                      {isPending ? (
                        <ClockIcon className={`text-amber-600 ${isPhone ? 'h-4 w-4' : 'h-5 w-5'}`} />
                      ) : isRejected ? (
                        <XCircleIcon className={`text-rose-500 ${isPhone ? 'h-4 w-4' : 'h-5 w-5'}`} />
                      ) : (
                        <ClockIcon className={`text-ink-400 ${isPhone ? 'h-4 w-4' : 'h-5 w-5'}`} />
                      )}
                    </div>

                    {/* 정보 */}
                    <div className="ml-3 flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`font-bold tracking-tight truncate ${!isPending ? 'text-ink-500' : 'text-ink-900'} ${isPhone ? 'text-sm' : ''}`}>
                          {invitation.email || invitation.phone || (invitation.username && `@${invitation.username}`)}
                        </span>
                        {(isRejected || isExpired) && (
                          <Badge tone={getInvitationStatusBadgeTone(invitation.status)} dot className="flex-none">
                            {isRejected ? '거절됨' : '만료됨'}
                          </Badge>
                        )}
                      </div>
                      <div className={`flex items-center gap-1.5 mt-0.5 flex-wrap ${isPhone ? 'text-[10px]' : 'text-xs'}`}>
                        <Badge tone={getMemberKindBadgeTone(isOwnerInvitation(invitation) ? 'owner' : 'member')} shape="rounded" className="flex-none">
                          {getInvitationKindLabel(invitation)}
                        </Badge>
                        {invitation.site && (
                          <Badge tone="emerald" shape="rounded" className="flex-none">
                            {invitation.site.name}
                          </Badge>
                        )}
                        <span className="font-medium tabular-nums text-ink-400">
                          {isPending ? '만료' : isRejected ? '거절' : '만료'}: {formatDateToLocal(new Date(isPending ? invitation.expiresAt : (invitation.updatedAt ?? invitation.createdAt)))}
                        </span>
                      </div>
                    </div>

                    {/* 액션 */}
                    {isPending && (
                      <div className="flex flex-col items-end gap-1 ml-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-brand-600 hover:bg-brand-50 hover:text-brand-700"
                          onClick={() => handlers.handleResendInvitation(invitation)}
                          disabled={modals.isResending === invitation.id}
                        >
                          {modals.isResending === invitation.id ? '...' : '재발송'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          onClick={() => handlers.setCancelTarget(invitation)}
                        >
                          취소
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* 모달들 */}
      <AllModals modals={modals} handlers={handlers} data={data} isMobile />
    </div>
  );
}

export default MembersMobile;
